import { z } from "zod";
import {
  PAST_DAYS_MIN,
  PAST_DAYS_MAX,
  PAST_DAYS_MIN_MESSAGE,
  PAST_DAYS_MAX_MESSAGE,
  PAST_DAYS_TYPE_MESSAGE,
} from "@/utils/pastDaysValidation";

import { type AcquirePolicy } from "./acquirePolicy";

// 識別名の上限と経過日数の範囲・文言は既存 /acquiremultiplebooks の FormDataSchema に揃える
export const bulkAcquireFormSchema = z
  .object({
    identifyName: z.string().max(256, "識別名は256文字以内です"),
    /** 取得条件の指定方法。legacy は経過日数だけを指定（自社取得済み → リユース → 新規で固定）、policy は下の4項目で組む */
    mode: z.enum(["legacy", "policy"]),
    /** 自社取得済み登記を再取得せず依頼に含める */
    useAcquired: z.boolean(),
    /** リユース登記を使う */
    useReuse: z.boolean(),
    /** 候補がない書類を新規取得する（false なら依頼から除く） */
    useNew: z.boolean(),
    /** 取得済み・リユースの経過日数の制限をしない */
    ignoreWindow: z.boolean(),
    pastDays: z.number({
      invalid_type_error: PAST_DAYS_TYPE_MESSAGE,
    }),
    /** 自分が取得済みの登記をチェック対象から外す（未取得のみを取得） */
    excludeAcquired: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!data.useAcquired && !data.useReuse && !data.useNew) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "取得する登記の種類を1つ以上選んでください",
        path: ["useNew"],
      });
    }
    // 経過日数を使わない条件では欄が表示されず値を直せないため、検証対象から外す
    if (data.ignoreWindow) return;
    if (data.pastDays < PAST_DAYS_MIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: PAST_DAYS_MIN_MESSAGE,
        path: ["pastDays"],
      });
    } else if (data.pastDays > PAST_DAYS_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: PAST_DAYS_MAX_MESSAGE,
        path: ["pastDays"],
      });
    }
  });

export type BulkAcquireFormData = z.infer<typeof bulkAcquireFormSchema>;

export type PolicyFields = Pick<
  BulkAcquireFormData,
  "useAcquired" | "useReuse" | "useNew" | "ignoreWindow"
>;

export const POLICY_FIELD_NAMES = [
  "useAcquired",
  "useReuse",
  "useNew",
  "ignoreWindow",
] as const satisfies ReadonlyArray<keyof PolicyFields>;

/**
 * フォーム値を取得条件に写す。探索順は自社取得済み → リユース → 新規で固定
 * （新規取得は候補が常に存在する終点なので末尾）
 *
 * @example
 * policyFromForm({ useAcquired: true, useReuse: true, useNew: false, ignoreWindow: false })
 * // { steps: ["acquired", "reuse"], ignoreWindow: false }
 */
export const policyFromForm = (values: PolicyFields): AcquirePolicy => ({
  steps: [
    ...(values.useAcquired ? (["acquired"] as const) : []),
    ...(values.useReuse ? (["reuse"] as const) : []),
    ...(values.useNew ? (["new"] as const) : []),
  ],
  ignoreWindow: values.ignoreWindow,
});

/**
 * 「未取得の登記・図面のみを取得」を指定できない条件か。
 * 取得方法を指定する方式で自社取得済みを使う条件は、未取得のみにすると自社取得済みのセルを選べず
 * 自社取得済みを使う選択と矛盾するため受け付けない。経過日数を指定する方式は自社取得済みを
 * 内部で先に拾うだけ（利用者が選んだ手段ではない）なので対象外
 */
export const isExcludeAcquiredLocked = (
  mode: BulkAcquireFormData["mode"],
  policy: AcquirePolicy
): boolean => mode === "policy" && policy.steps.includes("acquired");

/** 取得条件をフォーム値に写す（policyFromForm の逆向き） */
export const fieldsFromPolicy = ({
  steps,
  ignoreWindow,
}: AcquirePolicy): PolicyFields => ({
  useAcquired: steps.includes("acquired"),
  useReuse: steps.includes("reuse"),
  useNew: steps.includes("new"),
  ignoreWindow,
});
